// Typed client for the Groundbeam admin API. Every request carries the Clerk
// session JWT (with the active-org `o` claim); the API resolves the tenant
// from that org. Shapes mirror @groundbeam/contracts but are declared locally
// so the SPA bundle stays free of the server packages.

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export type DayKey =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export interface BusinessProfile {
  phone: string | null;
  email: string | null;
  address: {
    street: string | null;
    city: string | null;
    region: string | null;
    postcode: string | null;
    country: string | null;
  };
  hours: Partial<
    Record<DayKey, { opens: string | null; closes: string | null }>
  >;
  socials: {
    facebook: string | null;
    instagram: string | null;
    x: string | null;
    linkedin: string | null;
    youtube: string | null;
    tiktok: string | null;
  };
  gbpUrl: string | null;
}

export interface DomainStatus {
  domain: string | null;
  cnameTarget: string | null;
  status: "none" | "not_enabled" | "pending_dns" | "issuing" | "active";
  detail: string | null;
}

export interface CrmSettings {
  smsNotifyNumber: string | null;
  defaultCurrency: string;
  providers: { message: string; conversions: string };
  entitled: {
    smsNotifications: boolean;
    smsCrm: boolean;
    conversionUploads: boolean;
  };
}

export interface InboundSmsOutcome {
  kind: "won" | "lost" | "qualified" | "call" | "unrecognized" | "no_lead";
  leadId: string | null;
  newStatus: string | null;
  value: number | null;
  conversion: "queued" | "blocked_consent" | "exists" | "no_lead" | null;
}

export interface ProviderMessage {
  id: string;
  direction: "outbound" | "inbound";
  provider: string;
  to_number: string | null;
  from_number: string | null;
  body: string;
  created_at: number;
}

export interface ConversionUploadRow {
  id: number;
  platform: string;
  event_type: string;
  status: string;
  attempts: number;
  error_message: string | null;
  created_at: number;
  sent_at: number | null;
}

export interface Me {
  tenantId: string;
  role?: "admin" | "agent";
  /** Platform operator — unlocks the staff back-office view. */
  staff?: boolean;
  businessName: string | null;
  hostname: string | null;
  plan: string;
  billingState: string;
  trialEndsAt: string | null;
  entitlements: {
    features: Record<string, unknown>;
    limits: Record<string, unknown>;
  };
}

export type MigrationStatus = "queued" | "running" | "complete" | "failed";

export interface MigrationJob {
  id: string;
  tenantId: string;
  sourceUrl: string;
  hostname: string | null;
  status: MigrationStatus;
  phase: string | null;
  pagesDone: number;
  pagesTotal: number;
  needsRendering: number;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WordPressRestCmsEnrichment {
  source: "wordpress_rest";
  importRunId: string;
  siteId: string;
  detected: boolean;
  sourceName: string | null;
  sourceHome: string | null;
  restBase: string | null;
  totalDocuments: number;
  mapped: number;
  unmapped: number;
  rejected: number;
  collections: number;
  entries: number;
  errors: string[];
}

export type MirrorFidelityStatus = "queued" | "running" | "complete" | "failed";

export interface MirrorFidelityReport {
  v: 1;
  status: "not_run" | "passed" | "advisory" | "failed";
  lane: "verbatim" | "reconstruct" | "generate";
  previewUrl: string | null;
  summary: string | null;
  visual: {
    status: string;
    similarityMin: number;
    viewports: {
      viewport: string;
      /** Measured route; null/absent = the homepage (pre-per-route reports).
       *  Non-"/" entries are diagnosis and stay out of the headline score. */
      route?: string | null;
      similarity: number | null;
    }[];
  } | null;
  behavioral: {
    status: string;
    leadLanded: boolean;
    attributionCaptured: boolean;
    marker: string | null;
  } | null;
  performance: {
    status: string;
    lighthouse: {
      performance: number | null;
      accessibility: number | null;
      bestPractices: number | null;
      seo: number | null;
    } | null;
    lcpMs: number | null;
    scriptTransferBytes: number | null;
  } | null;
  links: {
    status: string;
    checked: number;
    broken: { url: string; status: number | null; reason: string | null }[];
  } | null;
  overrideNote: string | null;
  warnings: string[];
}

export interface MirrorFidelityCheck {
  id: string;
  tenantId: string;
  jobId: string;
  hostname: string;
  sourceUrl: string;
  status: MirrorFidelityStatus;
  report: MirrorFidelityReport | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MigrationImportStatus {
  job: MigrationJob;
  cmsEnrichment: { wordpressRest: WordPressRestCmsEnrichment | null };
  fidelity: MirrorFidelityCheck | null;
}

export interface ManagedAstroUploadFile {
  path: string;
  sizeBytes: number;
  mimeType: string | null;
  text: string | null;
}

export interface ManagedAstroImportPlan {
  source: "astro_project";
  astroDetected: boolean;
  packageManager: "pnpm" | "npm" | "yarn" | "bun" | "unknown";
  packageName: string | null;
  groundbeamKitDetected: boolean;
  sanity: {
    detected: boolean;
    dependencyNames: string[];
    configFiles: string[];
    schemaFiles: string[];
    queryFiles: string[];
    exportStrategy:
      | "none"
      | "dataset_export"
      | "dataset_export_or_public_query";
    projectId: string | null;
    dataset: string | null;
    apiVersion: string | null;
    targetCollections: string[];
  };
  environment: {
    referencedVars: string[];
    requiredVars: string[];
  };
  requiredBindings: string[];
  operations: {
    type: string;
    reason: string;
    required: boolean;
    files: string[];
  }[];
  verificationGates: string[];
}

export interface ManagedAstroImportAnalysis {
  id: string;
  tenantId: string;
  hostname: string | null;
  sourceLabel: string | null;
  status: "analysis_ready" | "rejected";
  plan: ManagedAstroImportPlan;
  summary: {
    totalFiles: number;
    textFiles: number;
    assetFiles: number;
    totalBytes: number;
    textBytes: number;
    assetBytes: number;
    formFileCount: number;
    analyticsFileCount: number;
    operationCount: number;
    requiredOperationCount: number;
    verificationGateCount: number;
  };
  warnings: string[];
  nextSteps: string[];
  createdAt: string;
  origin?: "upload" | "generated";
}

export interface GenerationBrief {
  businessName: string;
  description: string;
  industry?: string;
  tone?: string;
  language?: string;
  goals?: string[];
  pagesHint?: string[];
  contact?: {
    email?: string;
    phone?: string;
    address?: string;
  };
}

export type ManagedAstroImportStatus =
  | "analysis_ready"
  | "config_pending"
  | "queued"
  | "building"
  | "preview_ready"
  | "publish_ready"
  | "published"
  | "failed";

export type ManagedAstroPendingConfigKind =
  | "missing_env"
  | "sanity_token"
  | "sanity_export"
  | "form_mapping"
  | "analytics_review"
  | "custom_domain_dns";

export interface ManagedAstroPendingConfigQuestion {
  id: string;
  kind: ManagedAstroPendingConfigKind;
  title: string;
  description: string;
  requiredForPreview: boolean;
  requiredForPublish: boolean;
  status: "open" | "resolved";
}

export interface ManagedAstroBuildResult {
  status: "queued" | "running" | "passed" | "failed";
  ok: boolean;
  command: string;
  routes: string[];
  output: string;
  error: string | null;
  previewUrl: string | null;
  artifactPrefix: string | null;
  reportKey: string | null;
  builtAt: string | null;
}

export interface ManagedAstroRunnerReportGate {
  gate: string;
  status: "passed" | "failed" | "skipped";
  required: boolean;
  detail: string;
}

export interface ManagedAstroRunnerReport {
  v: 1;
  generatedAt: string;
  importId: string;
  tenantId: string;
  siteId: string;
  hostname: string | null;
  source: {
    downloadedFiles: number;
    fileCount: number;
    totalBytes: number;
    textBytes: number;
    binaryFileCount: number;
  };
  build: ManagedAstroBuildResult;
  verifier: {
    gates: ManagedAstroRunnerReportGate[];
    requiredPassed: boolean;
    publishAllowed: boolean;
  };
  preview: {
    status: "available" | "blocked";
    url: string | null;
    reason: string | null;
  };
  sanity: {
    status: "not_detected" | "not_provided" | "parsed" | "imported" | "failed";
    documentsTotal: number;
    error: string | null;
  };
  mediaManifest: {
    assets: Array<{ sourcePath: string; sizeBytes: number }>;
  };
  assetCopies: Array<{
    sourcePath: string;
    status: "planned" | "copied" | "failed";
    sizeBytes: number;
  }>;
  editableManifest: { entries: unknown[] };
  patches: Array<{
    afterBytes: number;
    beforeBytes: number;
    kind: string;
    path: string;
    reason: string;
  }>;
  visualQa: {
    status: "not_run" | "passed" | "failed";
    summary: string | null;
    warnings: string[];
    artifacts: Array<{
      path: string;
      key: string;
      contentType: string;
      sizeBytes: number;
      kind: "screenshot" | "comparison" | "report";
      label: string;
      viewport: {
        name: string;
        width: number;
        height: number;
      } | null;
    }>;
  };
  reportKey: string;
}

export interface ManagedAstroPublishedSite {
  importId: string;
  artifactPrefix: string;
  routes: string[];
  reportKey: string | null;
  sourceLabel: string | null;
  publishedAt: string;
}

export interface ManagedAstroPublishTarget {
  tenantHostname: string | null;
  customDomain: string | null;
  cnameTarget: string | null;
  dnsStatus: "not_configured" | "pending" | "ready";
}

export interface ManagedAstroImportRecord {
  id: string;
  tenantId: string;
  hostname: string | null;
  sourceLabel: string | null;
  status: ManagedAstroImportStatus;
  analysis: ManagedAstroImportAnalysis;
  pendingConfig: ManagedAstroPendingConfigQuestion[];
  build: ManagedAstroBuildResult | null;
  publishTarget: ManagedAstroPublishTarget;
  createdAt: string;
  updatedAt: string;
}

export interface ManagedAstroSourceArtifact {
  importId: string;
  tenantId: string;
  keyPrefix: string;
  manifestKey: string;
  fileCount: number;
  totalBytes: number;
  textBytes: number;
  binaryFileCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ManagedAstroEnvConfigSummary {
  key: string;
  providedVars: string[];
  updatedAt: string;
}

export interface ManagedAstroSourceUploadFile {
  file: File;
  path: string;
}

export type LeadStatus =
  | "new"
  | "contacted"
  | "qualified"
  | "won"
  | "lost"
  | "spam";

export interface Lead {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  message: string | null;
  status: LeadStatus;
  value: number | null;
  click_id_type: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  landing_url: string | null;
  referrer: string | null;
  created_at: number;
  updated_at: number;
}

export type ConversationStatus = "open" | "needs_owner" | "closed" | "spam";
export type ConversationChannel = "webchat" | "sms" | "whatsapp";
export type ConversationAuthor = "visitor" | "owner" | "assistant";

export interface Conversation {
  id: string;
  channel: ConversationChannel;
  status: ConversationStatus;
  lead_id: string | null;
  click_id_type: string | null;
  utm_source: string | null;
  utm_campaign: string | null;
  landing_url: string | null;
  created_at: number;
  updated_at: number;
  last_message: string;
  message_count: number;
  last_author: "visitor" | "owner" | "assistant" | null;
}

export interface ConversationMessage {
  id: string;
  author: ConversationAuthor;
  body: string;
  created_at: number;
}

export interface KbDocument {
  id: string;
  title: string;
  body: string;
  enabled: 0 | 1;
  created_at: number;
  updated_at: number;
}

export type KbDocumentPatch = Partial<
  Pick<KbDocument, "title" | "body"> & { enabled: boolean }
>;

export interface LeadAnalytics {
  days: number;
  totals: {
    leads: number;
    wonValue: number;
    byStatus: Record<string, number>;
  };
  sources: { source: string; leads: number; won: number; wonValue: number }[];
  daily: { day: string; leads: number }[];
}

export interface LeadEvent {
  id: number;
  event_type: string;
  old_value: string | null;
  new_value: string | null;
  note: string | null;
  timestamp: string | number;
}

export interface PageSeo {
  metaTitle: string;
  metaDescription: string;
  ogImage?: string;
  noIndex?: boolean;
}

export interface PageBlock {
  id: string;
  blockType: string;
  variant: string;
  data: Record<string, unknown>;
  order: number;
}

export interface AiEditTarget {
  blockId: string;
  path: string;
  label?: string;
  kind: "image" | "link" | "text" | "textarea";
  hrefPath?: string;
  altPath?: string;
}

export interface AiEditChatTurn {
  content: string;
  role: "assistant" | "user";
}

export type AiEditPlanSource = "fallback" | "local" | "model";

export interface EditorPage {
  id: string;
  siteId: string;
  slug: string;
  title: string;
  seo: PageSeo;
  blocks: PageBlock[];
  status: "draft" | "published";
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  hasCapturedSnapshot: boolean;
  sourceUrl: string | null;
  warnings: string[];
  renderMode: "mixed" | "editable_blocks" | "captured_mirror" | "empty";
}

export interface BlogPostContent {
  body: string;
  format: "markdown" | "plain";
}

export interface BlogPost {
  id: string;
  siteId: string;
  slug: string;
  title: string;
  excerpt: string | null;
  featuredImage: string | null;
  content: BlogPostContent;
  author: string | null;
  tags: string[];
  seo: PageSeo;
  status: "draft" | "published";
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BlogPostInput {
  title?: string;
  slug?: string;
  excerpt?: string | null;
  featuredImage?: string | null;
  content?: BlogPostContent;
  author?: string | null;
  tags?: string[];
  seo?: PageSeo;
  status?: "draft" | "published";
}

export interface MediaItem {
  id: string;
  siteId: string;
  filename: string;
  key: string;
  url: string;
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  alt: string | null;
  createdAt: string;
}

export interface MediaStorage {
  bytesUsed: number;
  limitBytes: number | null;
}

export interface CmsImportRun {
  id: string;
  siteId: string;
  source: "sanity_dataset_export" | "wordpress_rest";
  sourceRef: string | null;
  status: "running" | "complete" | "failed";
  totals: {
    totalDocuments: number;
    mapped: number;
    unmapped: number;
    rejected: number;
  };
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImportedCmsEntry {
  id: string;
  siteId: string;
  collectionId: string;
  importRunId: string | null;
  source: "sanity_dataset_export" | "wordpress_rest";
  sourceId: string;
  sourceType: string;
  name: string;
  slug: string | null;
  data: Record<string, unknown>;
  raw: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImportedCmsCollection {
  id: string;
  siteId: string;
  importRunId: string | null;
  source: "sanity_dataset_export" | "wordpress_rest";
  sourceType: string;
  key: string;
  label: string;
  itemLabel: string | null;
  description: string | null;
  documentCount: number;
  createdAt: string;
  updatedAt: string;
  entries: ImportedCmsEntry[];
}

export interface ImportedCms {
  site: { siteId: string; hostname: string };
  collections: ImportedCmsCollection[];
  importRuns: CmsImportRun[];
}

export interface PreviewResult {
  html: string;
  errors: { blockType: string; error: string }[];
  scriptBytes: number;
  warnings: string[];
}

export interface ThemeInput {
  name: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textMuted: string;
    border: string;
    success: string;
    error: string;
  };
  typography: {
    headingFont: string;
    bodyFont: string;
    baseSizePx: number;
    scaleRatio: number;
    headingWeight: number;
    bodyWeight: number;
    lineHeight: number;
  };
  shape: {
    borderRadiusSm: string;
    borderRadiusMd: string;
    borderRadiusLg: string;
    buttonRadius: string;
    cardRadius: string;
    inputRadius: string;
  };
  spacing: {
    sectionPaddingY: string;
    containerMaxWidth: string;
    containerPaddingX: string;
  };
  shadows: { sm: string; md: string; lg: string };
  buttonStyles: {
    primaryBg: string;
    primaryText: string;
    primaryHoverBg: string;
    secondaryBg: string;
    secondaryText: string;
    secondaryBorder: string;
    paddingX: string;
    paddingY: string;
    fontWeight: number;
    textTransform: "none" | "uppercase" | "lowercase" | "capitalize";
    letterSpacing: string;
  };
}

/** Sparse per-customer entitlement overrides (staff back-office). */
export interface StaffOverrides {
  features: Record<string, boolean>;
  limits: Record<string, number | null>;
}

export interface StaffCustomerSummary {
  id: string;
  businessName: string | null;
  email: string;
  hostname: string | null;
  customDomain: string | null;
  plan: string;
  billingState: string;
  trialEndsAt: string | null;
  provisionedAt: string | null;
  overrides: StaffOverrides;
  createdAt: string;
}

export interface StaffCustomerDetail {
  customer: {
    id: string;
    businessName: string | null;
    email: string;
    hostname: string | null;
    customDomain: string | null;
    plan: string;
    billingState: string;
    trialEndsAt: string | null;
    country: string | null;
    provisionedAt: string | null;
    createdAt: string;
  };
  overrides: StaffOverrides;
  effective: {
    features: Record<string, boolean>;
    limits: Record<string, number | null>;
  };
}

export interface StaffOverridesResult {
  overrides: StaffOverrides;
  effective: StaffCustomerDetail["effective"];
  renderConfig: {
    refreshed: string[];
    unchanged: string[];
    skipped: string[];
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Server is setting the workspace up — poll, don't error. */
    readonly provisioning = false,
  ) {
    super(message);
  }
}

export type GetToken = () => Promise<string | null>;

export function createApi(getToken: GetToken) {
  async function parseResponse<T>(res: Response): Promise<T> {
    if (!res.ok) {
      let detail = res.statusText;
      let provisioning = false;
      try {
        const body = (await res.json()) as {
          error?: string;
          provisioning?: boolean;
        };
        if (body.error) detail = body.error;
        provisioning = body.provisioning === true;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(res.status, detail, provisioning);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  async function req<T>(path: string, init?: RequestInit): Promise<T> {
    const token = await getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
    return parseResponse<T>(res);
  }

  async function reqText(path: string, init?: RequestInit): Promise<string> {
    const token = await getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
    if (!res.ok) {
      throw new ApiError(res.status, res.statusText);
    }
    return res.text();
  }

  async function upload<T>(
    path: string,
    form: FormData,
    init?: Omit<RequestInit, "body" | "method">,
  ): Promise<T> {
    const token = await getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      method: "POST",
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
      body: form,
    });
    return parseResponse<T>(res);
  }

  return {
    me: () => req<Me>("/v1/me"),
    staffCustomers: () =>
      req<{ customers: StaffCustomerSummary[] }>("/v1/staff/customers"),
    staffCustomer: (id: string) =>
      req<StaffCustomerDetail>(`/v1/staff/customers/${id}`),
    staffSetOverrides: (id: string, overrides: StaffOverrides) =>
      req<StaffOverridesResult>(`/v1/staff/customers/${id}/entitlements`, {
        method: "PATCH",
        body: JSON.stringify(overrides),
      }),
    staffRenderConfigSweep: () =>
      req<{
        tenants: number;
        refreshedHosts: number;
        unchangedHosts: number;
        errors: { tenant: string; error: string }[];
      }>("/v1/staff/render-config/sweep", { method: "POST" }),
    chatEdit: (message: string) =>
      req<{
        reply: string;
        applied: { texts: number; themeColors: number };
      }>("/v1/sites/chat-edit", {
        method: "POST",
        body: JSON.stringify({ message }),
      }),
    startImport: (url: string) =>
      req<{ jobId: string; status: string }>("/v1/sites/import", {
        method: "POST",
        body: JSON.stringify({ url }),
      }),
    startGeneration: (brief: GenerationBrief) =>
      req<{ importId: string; status: string }>("/v1/sites/generate", {
        method: "POST",
        body: JSON.stringify({ brief }),
      }),
    getImport: (id: string) =>
      req<MigrationImportStatus>(`/v1/sites/import/${id}`),
    listImports: () => req<{ jobs: MigrationJob[] }>("/v1/sites/imports"),
    publishMirrorImport: (
      id: string,
      body?: { fidelityOverride?: { note: string } },
    ) =>
      req<{ published: boolean; publishedCount: number; url: string }>(
        `/v1/sites/import/${id}/publish`,
        {
          method: "POST",
          body: JSON.stringify(body ?? {}),
        },
      ),
    analyzeManagedAstroImport: (payload: {
      files: ManagedAstroUploadFile[];
      sourceLabel?: string | null;
    }) =>
      req<{
        analysis: ManagedAstroImportAnalysis;
        import: ManagedAstroImportRecord;
      }>("/v1/sites/managed-astro/analyze", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    listManagedAstroImports: () =>
      req<{ imports: ManagedAstroImportRecord[] }>(
        "/v1/sites/managed-astro/imports",
      ),
    getManagedAstroImport: (id: string) =>
      req<{ import: ManagedAstroImportRecord }>(
        `/v1/sites/managed-astro/imports/${id}`,
      ),
    getManagedAstroBuildReport: (id: string) =>
      req<{ report: ManagedAstroRunnerReport }>(
        `/v1/sites/managed-astro/imports/${id}/build-report`,
      ),
    uploadManagedAstroSource: (
      id: string,
      payload: {
        files: ManagedAstroUploadFile[];
        sourceFiles: ManagedAstroSourceUploadFile[];
      },
    ) => {
      const form = new FormData();
      form.set("manifest", JSON.stringify({ files: payload.files }));
      payload.sourceFiles.forEach((sourceFile, index) => {
        form.set(`file:${index}`, sourceFile.file, sourceFile.path);
      });
      return upload<{
        import: ManagedAstroImportRecord;
        sourceArtifact: ManagedAstroSourceArtifact;
      }>(`/v1/sites/managed-astro/imports/${id}/source`, form);
    },
    resolveManagedAstroEnvConfig: (
      id: string,
      payload: { variables: Record<string, string> },
    ) =>
      req<{
        envConfig: ManagedAstroEnvConfigSummary;
        import: ManagedAstroImportRecord;
      }>(`/v1/sites/managed-astro/imports/${id}/config/env`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    queueManagedAstroBuild: (id: string) =>
      req<{
        import: ManagedAstroImportRecord;
        sourceArtifact: ManagedAstroSourceArtifact;
      }>(`/v1/sites/managed-astro/imports/${id}/build`, {
        method: "POST",
      }),
    publishManagedAstroImport: (id: string) =>
      req<{
        import: ManagedAstroImportRecord;
        managedAstro: ManagedAstroPublishedSite;
        publishedUrl: string;
      }>(`/v1/sites/managed-astro/imports/${id}/publish`, {
        method: "POST",
      }),
    leads: () => req<{ leads: Lead[] }>("/v1/leads"),
    conversations: () =>
      req<{ conversations: Conversation[] }>("/v1/conversations"),
    saveConversationAsLead: (id: string, details: { name?: string } = {}) =>
      req<{ leadId: string | null; existing: boolean }>(
        `/v1/conversations/${id}/save-lead`,
        { method: "POST", body: JSON.stringify(details) },
      ),
    chatAiMode: () =>
      req<{ mode: "off" | "when_away" | "always_first"; available: boolean }>(
        "/v1/chat-ai",
      ),
    setChatAiMode: (mode: "off" | "when_away" | "always_first") =>
      req<{ mode: string }>("/v1/chat-ai", {
        method: "PATCH",
        body: JSON.stringify({ mode }),
      }),
    kbDocuments: () => req<{ documents: KbDocument[] }>("/v1/kb"),
    createKbDocument: (title: string, body: string) =>
      req<{ document: KbDocument }>("/v1/kb", {
        method: "POST",
        body: JSON.stringify({ title, body }),
      }),
    updateKbDocument: (id: string, patch: KbDocumentPatch) =>
      req<{ document: KbDocument }>(`/v1/kb/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    deleteKbDocument: (id: string) =>
      req<{ deleted: boolean }>(`/v1/kb/${id}`, { method: "DELETE" }),
    conversationMessages: (id: string) =>
      req<{ messages: ConversationMessage[] }>(
        `/v1/conversations/${id}/messages`,
      ),
    replyToConversation: (id: string, message: string) =>
      req<{ sent: ConversationMessage }>(`/v1/conversations/${id}/reply`, {
        method: "POST",
        body: JSON.stringify({ message }),
      }),
    updateConversation: (id: string, payload: { status: ConversationStatus }) =>
      req<{ updated: Conversation }>(`/v1/conversations/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    presence: () => req<{ ok: boolean }>("/v1/presence", { method: "POST" }),
    getDomain: () => req<DomainStatus>("/v1/domains"),
    connectDomain: (domain: string) =>
      req<DomainStatus>("/v1/domains", {
        method: "POST",
        body: JSON.stringify({ domain }),
      }),
    disconnectDomain: () =>
      req<DomainStatus>("/v1/domains", { method: "DELETE" }),
    getBusinessProfile: () =>
      req<{ businessName: string | null; profile: BusinessProfile }>(
        "/v1/business-profile",
      ),
    saveBusinessProfile: (profile: BusinessProfile) =>
      req<{ profile: BusinessProfile }>("/v1/business-profile", {
        method: "PUT",
        body: JSON.stringify(profile),
      }),
    getBrand: () =>
      req<{
        businessName: string | null;
        logoUrl: string | null;
        faviconUrl: string | null;
        primaryColor: string | null;
      }>("/v1/brand"),
    setBrand: (patch: {
      logoUrl?: string | null;
      faviconUrl?: string | null;
      primaryColor?: string | null;
    }) =>
      req<{
        businessName: string | null;
        logoUrl: string | null;
        faviconUrl: string | null;
        primaryColor: string | null;
      }>("/v1/brand", { method: "PATCH", body: JSON.stringify(patch) }),
    crmSettings: () => req<CrmSettings>("/v1/crm/settings"),
    setCrmSettings: (patch: {
      smsNotifyNumber?: string | null;
      defaultCurrency?: string;
    }) =>
      req<{ smsNotifyNumber: string | null; defaultCurrency: string }>(
        "/v1/crm/settings",
        { method: "PATCH", body: JSON.stringify(patch) },
      ),
    simulateInboundSms: (body: string) =>
      req<InboundSmsOutcome>("/v1/crm/simulate-inbound", {
        method: "POST",
        body: JSON.stringify({ body }),
      }),
    leadClosedLoop: (id: string) =>
      req<{
        messages: ProviderMessage[];
        conversions: ConversionUploadRow[];
      }>(`/v1/leads/${id}/closed-loop`),
    pushKey: () => req<{ key: string }>("/v1/push/key"),
    pushSubscribe: (subscription: {
      endpoint: string;
      keys: { p256dh: string; auth: string };
    }) =>
      req<{ subscribed: boolean }>("/v1/push/subscribe", {
        method: "POST",
        body: JSON.stringify(subscription),
      }),
    pushUnsubscribe: (endpoint: string) =>
      req<{ subscribed: boolean }>("/v1/push/subscribe", {
        method: "DELETE",
        body: JSON.stringify({ endpoint }),
      }),
    leadAnalytics: (days = 30) =>
      req<LeadAnalytics>(`/v1/leads/analytics?days=${days}`),
    addLeadNote: (id: string, note: string) =>
      req<{ event: LeadEvent }>(`/v1/leads/${id}/notes`, {
        method: "POST",
        body: JSON.stringify({ note }),
      }),
    leadEvents: (id: string) =>
      req<{ events: LeadEvent[] }>(`/v1/leads/${id}/events`),
    exportLeadsCsv: () => reqText("/v1/leads/export.csv"),
    updateLead: (
      id: string,
      payload: { status?: LeadStatus; value?: number | null },
    ) =>
      req<{ lead: Lead }>(`/v1/leads/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    listPages: () =>
      req<{
        site: {
          siteId: string;
          hostname: string;
          /** > 0 ⇔ a preview snapshot exists (gates "Open preview"). */
          previewEpoch?: number;
        };
        pages: EditorPage[];
      }>("/v1/sites/pages"),
    listBlogPosts: () =>
      req<{
        site: { siteId: string; hostname: string };
        posts: BlogPost[];
      }>("/v1/sites/blog-posts"),
    listMedia: () =>
      req<{
        site: { siteId: string; hostname: string };
        items: MediaItem[];
        storage: MediaStorage;
      }>("/v1/sites/media"),
    getImportedCms: () => req<ImportedCms>("/v1/sites/cms"),
    updateImportedCmsEntry: (
      id: string,
      payload: Partial<Pick<ImportedCmsEntry, "data" | "name" | "slug">>,
    ) =>
      req<{ entry: ImportedCmsEntry }>(`/v1/sites/cms/entries/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    uploadMedia: (file: File, alt?: string) => {
      const form = new FormData();
      form.set("file", file);
      if (alt?.trim()) form.set("alt", alt.trim());
      return upload<{ item: MediaItem }>("/v1/sites/media", form);
    },
    updateMedia: (
      id: string,
      payload: Partial<Pick<MediaItem, "alt" | "filename">>,
    ) =>
      req<{ item: MediaItem }>(`/v1/sites/media/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    deleteMedia: (id: string) =>
      req<void>(`/v1/sites/media/${id}`, { method: "DELETE" }),
    createBlogPost: (payload: BlogPostInput & { title: string }) =>
      req<{ post: BlogPost }>("/v1/sites/blog-posts", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    getBlogPost: (id: string) =>
      req<{ post: BlogPost }>(`/v1/sites/blog-posts/${id}`),
    updateBlogPost: (id: string, payload: BlogPostInput) =>
      req<{ post: BlogPost }>(`/v1/sites/blog-posts/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    deleteBlogPost: (id: string) =>
      req<void>(`/v1/sites/blog-posts/${id}`, { method: "DELETE" }),
    createPage: (payload: { slug?: string; title: string }) =>
      req<{ page: EditorPage }>("/v1/sites/pages", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    getPage: (id: string) => req<{ page: EditorPage }>(`/v1/sites/pages/${id}`),
    rebuildPage: (id: string) =>
      req<{ page: EditorPage }>(`/v1/sites/pages/${id}/rebuild`, {
        method: "POST",
      }),
    updatePage: (
      id: string,
      payload: Partial<Pick<EditorPage, "title" | "seo" | "blocks">>,
    ) =>
      req<{ page: EditorPage }>(`/v1/sites/pages/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    previewPage: (
      id: string,
      payload: Partial<Pick<EditorPage, "title" | "seo" | "blocks">> & {
        theme?: ThemeInput;
      },
    ) =>
      req<PreviewResult>(`/v1/sites/pages/${id}/preview`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    aiEditPage: (
      id: string,
      payload: Partial<Pick<EditorPage, "title" | "seo" | "blocks">> & {
        chatHistory?: AiEditChatTurn[];
        insertAfterBlockId?: string;
        prompt: string;
        target?: AiEditTarget;
        theme?: ThemeInput;
      },
    ) =>
      req<{
        page: EditorPage;
        message: string;
        planSource: AiEditPlanSource;
      }>(`/v1/sites/pages/${id}/ai-edit`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    addSection: (id: string, prompt: string) =>
      req<{ page: EditorPage }>(`/v1/sites/pages/${id}/sections`, {
        method: "POST",
        body: JSON.stringify({ prompt }),
      }),
    publishSite: () =>
      req<{
        blogPostCount: number;
        blogUrls: string[];
        pages: EditorPage[];
        publishedCount: number;
        urls: string[];
      }>("/v1/sites/publish", { method: "POST" }),
    publishPage: (id: string) =>
      req<{ page: EditorPage; url: string }>(`/v1/sites/pages/${id}/publish`, {
        method: "POST",
      }),
    updatePreview: () =>
      req<{
        blogPostCount: number;
        pageCount: number;
        previewUrl: string;
        skippedPages: { pageTitle: string; error: string }[];
      }>("/v1/sites/preview", { method: "POST" }),
    getTheme: () => req<{ theme: ThemeInput }>("/v1/sites/theme"),
    saveTheme: (theme: ThemeInput) =>
      req<{ theme: ThemeInput }>("/v1/sites/theme", {
        method: "PATCH",
        body: JSON.stringify({ theme }),
      }),
    listRedirects: () =>
      req<{ redirects: SiteRedirect[] }>("/v1/sites/redirects"),
    createRedirect: (payload: {
      sourcePath: string;
      targetUrl: string;
      statusCode?: 301 | 302 | 307 | 308;
    }) =>
      req<{ redirects: SiteRedirect[] }>("/v1/sites/redirects", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    deleteRedirect: (id: string) =>
      req<void>(`/v1/sites/redirects/${id}`, { method: "DELETE" }),
    pageVersions: (pageId: string) =>
      req<{ versions: PageVersionSummary[] }>(
        `/v1/sites/pages/${pageId}/versions`,
      ),
    restorePageVersion: (pageId: string, versionId: number) =>
      req<{ page: EditorPage; restoredVersionId: number }>(
        `/v1/sites/pages/${pageId}/versions/${versionId}/restore`,
        { method: "POST" },
      ),
  };
}

export interface SiteRedirect {
  id: string;
  siteId: string;
  sourcePath: string;
  targetUrl: string;
  statusCode: 301 | 302 | 307 | 308;
  createdAt: string;
  updatedAt: string;
}

export interface PageVersionSummary {
  id: number;
  createdAt: string;
  publishedBy: string | null;
  blockCount: number;
}

export type Api = ReturnType<typeof createApi>;

/**
 * The preview host for a live tenant hostname — the noindex sibling that
 * serves the current draft. Mirrors contracts.previewHostname (admin-ui
 * declares its shapes locally rather than importing @groundbeam/contracts):
 * `plumber.groundbeam.site` → `https://plumber-preview.groundbeam.site/`.
 */
export function previewUrlFor(hostname: string): string {
  const host = hostname.toLowerCase();
  const dot = host.indexOf(".");
  const label = dot > 0 ? host.slice(0, dot) : host;
  const rest = dot > 0 ? host.slice(dot) : "";
  return `https://${label}-preview${rest}/`;
}
